import { useEffect, useMemo, useState, FormEvent } from 'react';
import { client } from '../../sdkAdapter';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import Pagination from "../ui/pagination";
import CatalogModalFrame from './CatalogModalFrame';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from './catalogAlerts';

interface TagRecord {
    id: number;
    name: string;
    active?: boolean | number | null;
    sort?: number | null;
    created_at?: string;
    updated_at?: string;
}

interface TagFormState {
    name: string;
    active: boolean;
    sort: string;
}

interface ValidatedTagForm {
    name: string;
    active: boolean;
    sort?: number;
}

const emptyForm = (): TagFormState => ({
    name: '',
    active: true,
    sort: '0',
});

const toBoolean = (value: unknown): boolean => value === true || value === 1 || value === '1';

const extractTagList = (response: any): Array<Pick<TagRecord, 'id' | 'name'>> => {
    const payload = response?.data?.data ?? response?.data ?? response;

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

const extractTagDetail = (response: any): TagRecord | null => {
    const payload = response?.data?.data ?? response?.data ?? response;

    if (Array.isArray(payload)) {
        return payload[0] ?? null;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data[0] ?? null;
    }

    if (payload && typeof payload === 'object') {
        return payload;
    }

    return null;
};

const getApiErrorMessage = (err: any, fallback: string): string => {
    const message = err?.response?.data?.messages || err?.response?.data?.message || err?.message;

    if (Array.isArray(message)) {
        return message.join(', ');
    }

    return typeof message === 'string' && message.trim() ? message : fallback;
};

const validateTagForm = (form: TagFormState): { valid: true; data: ValidatedTagForm; } | { valid: false; message: string; } => {
    const name = form.name.trim();
    const sortValue = form.sort.trim();

    if (!name) {
        return { valid: false, message: 'Tag name is required.' };
    }

    let sort: number | undefined;
    if (sortValue) {
        sort = Number(sortValue);
        if (!Number.isInteger(sort) || sort < 0) {
            return { valid: false, message: 'Sort order must be a whole number greater than or equal to 0.' };
        }
    }

    return {
        valid: true,
        data: {
            name,
            active: form.active,
            ...(typeof sort === 'number' ? { sort } : {}),
        }
    };
};

export default function CatalogTags() {
    const [allTags, setAllTags] = useState<TagRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState<TagFormState>(emptyForm);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const totalItems = allTags.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    const tags = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return allTags.slice(startIndex, startIndex + rowsPerPage);
    }, [allTags, currentPage, rowsPerPage]);

    useEffect(() => {
        refreshTags();
    }, []);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const loadTags = async (): Promise<TagRecord[]> => {
        const listResponse = await client.getApiCatalogTags({}, {
            params: { list: 1 }
        }) as any;

        const tagList = extractTagList(listResponse);

        if (tagList.length === 0) {
            return [];
        }

        const detailResponses = await Promise.allSettled(
            tagList.map((tag) => client.getApiCatalogTagId({ id: String(tag.id) }) as Promise<any>)
        );

        return detailResponses.map((result, index) => {
            const fallbackTag = tagList[index];

            if (result.status !== 'fulfilled') {
                return {
                    id: fallbackTag.id,
                    name: fallbackTag.name,
                };
            }

            const detailTag = extractTagDetail(result.value);
            return {
                id: fallbackTag.id,
                name: fallbackTag.name,
                ...detailTag,
            };
        });
    };

    const refreshTags = async (options?: { focusTagId?: number; targetPage?: number; }) => {
        setLoading(true);
        try {
            const nextTags = await loadTags();
            setAllTags(nextTags);

            if (options?.focusTagId) {
                const focusedIndex = nextTags.findIndex((tag) => tag.id === options.focusTagId);
                if (focusedIndex >= 0) {
                    setCurrentPage(Math.floor(focusedIndex / rowsPerPage) + 1);
                    return;
                }
            }

            if (options?.targetPage) {
                const nextTotalPages = Math.max(1, Math.ceil(nextTags.length / rowsPerPage));
                setCurrentPage(Math.min(options.targetPage, nextTotalPages));
            }
        } catch (err) {
            console.error('Failed to fetch tags:', err);
            setAllTags([]);
            await showErrorAlert(getApiErrorMessage(err, 'Failed to load tags'));
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (tag: TagRecord) => {
        setEditingId(tag.id);
        setForm({
            name: tag.name || '',
            active: toBoolean(tag.active ?? true),
            sort: typeof tag.sort === 'number' ? String(tag.sort) : '0',
        });
        setIsModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(emptyForm());
        setIsModalOpen(false);
    };

    const createTag = async () => {
        const validated = validateTagForm(form);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            const response = await client.postApiCatalogTagCreate({
                postApiCatalogTagCreateRequest: validated.data
            }) as any;

            const createdTagId = Number(response?.data?.data?.id ?? response?.data?.id);

            handleCancelEdit();
            await refreshTags(Number.isFinite(createdTagId) ? { focusTagId: createdTagId } : undefined);
            showSuccessToast('Tag created successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to create tag'));
        } finally {
            setSubmitting(false);
        }
    };

    const updateTag = async () => {
        const validated = validateTagForm(form);
        if (!validated.valid || editingId === null) {
            await showValidationAlert(validated.valid ? 'Tag ID is missing.' : validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogTagUpdate({
                postApiCatalogTagUpdateRequest: {
                    id: editingId,
                    ...validated.data,
                }
            });

            handleCancelEdit();
            await refreshTags({ focusTagId: editingId });
            showSuccessToast('Tag updated successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to update tag'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (editingId !== null) {
            await updateTag();
            return;
        }

        await createTag();
    };

    const handleDelete = async (tagId: number) => {
        const isConfirmed = await confirmAction({
            text: 'This tag will be permanently deleted.',
            confirmButtonText: 'Delete Tag'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCatalogTagDelete({
                postApiCatalogTagDeleteRequest: { id: tagId }
            });

            if (editingId === tagId) {
                handleCancelEdit();
            }

            const nextPage = tags.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
            await refreshTags({ targetPage: nextPage });
            showSuccessToast('Tag deleted successfully');
        } catch (err: any) {
            console.error(err);
            await showErrorAlert(getApiErrorMessage(err, 'Failed to delete tag'));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Tags</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Manage product tags, activation state, and sort order from the admin panel.
                    </p>
                </div>
                <button
                    onClick={() => {
                        handleCancelEdit();
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95"
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Tag
                </button>
            </div>

            <CatalogModalFrame
                isOpen={isModalOpen}
                onClose={handleCancelEdit}
                title={editingId !== null ? 'Edit Tag' : 'Create Tag'}
                className="max-w-xl"
                formId="catalog-tag-form"
                submitLabel={submitting ? 'Processing...' : editingId !== null ? 'Update Tag' : 'Create Tag'}
                submitDisabled={submitting}
            >
                <form id="catalog-tag-form" onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            placeholder="e.g. Summer Sale"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Sort Order</label>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                value={form.sort}
                                onChange={(e) => setForm({ ...form, sort: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="0"
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Status</label>
                            <select
                                value={form.active ? 'true' : 'false'}
                                onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}
                                className="w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>
                </form>
            </CatalogModalFrame>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="border-b border-gray-200 bg-gray-50/50 p-5 dark:border-gray-800 dark:bg-gray-800/50">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Existing Tags</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {loading ? 'Loading tag records...' : `${totalItems} tag${totalItems === 1 ? '' : 's'} loaded.`}
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-400">Name</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Sort</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Status</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-end text-xs font-bold uppercase tracking-wider text-gray-400">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="px-6 py-12 text-center font-medium text-gray-500 dark:text-gray-400">
                                        Loading tags...
                                    </TableCell>
                                </TableRow>
                            ) : tags.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="px-6 py-12 text-center font-medium text-gray-500 dark:text-gray-400">
                                        No tags found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tags.map((tag) => (
                                    <TableRow key={tag.id} className={`group transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 ${editingId === tag.id ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''}`}>
                                        <TableCell className="px-6 py-4 font-bold text-gray-700 dark:text-gray-200">{tag.name}</TableCell>
                                        <TableCell className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                                            {typeof tag.sort === 'number' ? tag.sort : 0}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <Badge color={toBoolean(tag.active ?? true) ? 'success' : 'error'} size="sm">
                                                {toBoolean(tag.active ?? true) ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-end">
                                            <div className="flex justify-end gap-3 opacity-0 transition-opacity group-hover:opacity-100">
                                                <button
                                                    onClick={() => handleEdit(tag)}
                                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-brand-200 hover:text-brand-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                    title="Edit Tag"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(tag.id)}
                                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-red-200 hover:text-red-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                    title="Delete Tag"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {!loading && totalItems > 0 ? (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                ) : null}
            </div>
        </div>
    );
}
